import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFatturePassive, syncFatturePassive, updateFatturaPassiva, exportFatturePassiveExcel, getFireflyAccounts, getFireflyAccountMapping, registraFatturaPassivaFirefly, annullaRegistrazioneFatturaFirefly } from '../services/api';
import { Table, Form, Spinner, Alert, Row, Col, Pagination, Card, Button, Badge, Dropdown, Modal, InputGroup } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faSync, faCalendar, faFileExcel, faEdit, faExclamationTriangle, faCheckCircle, faClock, faTimesCircle, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import SortableHeader from '../common/SortableHeader';
import { buildServerFilters } from '../common/serverFilterUtils';

const columnHelper = createColumnHelper();

const STATO_PAGAMENTO_OPTIONS = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'da_pagare', label: 'Da pagare' },
  { value: 'pagata', label: 'Pagata' },
  { value: 'parziale', label: 'Parziale' },
  { value: 'contestata', label: 'Contestata' },
];

const CATEGORIA_OPTIONS = [
  { value: '', label: 'Tutte le categorie' },
  { value: 'acquisto_film', label: 'MC - Acquisto Film' },
  { value: 'acquisto_musica', label: 'MC - Acquisto Musica' },
  { value: 'materiali_servizi_riversamenti', label: 'MC - Materiali Servizi e Riversamenti' },
  { value: 'spese_imballaggio', label: 'MC - Spese Imballaggio' },
  { value: 'marketing_pubblicita', label: 'MC - Spese Marketing e Pubblicità' },
  { value: 'spese_spedizioni', label: 'MC - Spese Spedizioni' },
  { value: 'commissioni_marketplace', label: 'MC - Commissioni Marketplace' },
  { value: 'commissioni_bancarie', label: 'MC - Commissioni Bancarie' },
];

const STATO_PAGAMENTO_FILTER_OPTIONS = STATO_PAGAMENTO_OPTIONS.filter(o => o.value);
const CATEGORIA_FILTER_OPTIONS = CATEGORIA_OPTIONS.filter(o => o.value);

const FILTER_VARIANTS = {
  stato_pagamento: { filterVariant: 'select', filterOptions: STATO_PAGAMENTO_FILTER_OPTIONS },
  categoria: { filterVariant: 'select', filterOptions: CATEGORIA_FILTER_OPTIONS },
  data_documento: { filterVariant: 'date-range' },
  data_scadenza: { filterVariant: 'date-range' },
  data_ricezione: { filterVariant: 'date-range' },
  importo_totale: { filterVariant: 'number-range' },
};

const STATO_CONFIG = {
  da_pagare: { label: 'Da pagare', bg: 'warning', icon: faClock },
  pagata: { label: 'Pagata', bg: 'success', icon: faCheckCircle },
  parziale: { label: 'Parziale', bg: 'info', icon: faClock },
  contestata: { label: 'Contestata', bg: 'danger', icon: faTimesCircle },
};

const CATEGORIA_TO_MAPPING_FIELD = {
  acquisto_film: 'expense_film_acquisto_account_name',
  acquisto_musica: 'expense_musica_acquisto_account_name',
  materiali_servizi_riversamenti: 'expense_materiali_servizi_riversamenti_account_name',
  spese_imballaggio: 'expense_spese_imballaggio_account_name',
  marketing_pubblicita: 'expense_marketing_pubblicita_account_name',
  spese_spedizioni: 'expense_spese_spedizioni_account_name',
  commissioni_marketplace: 'expense_commissioni_marketplace_account_name',
  commissioni_bancarie: 'expense_commissioni_bancarie_account_name',
};

const allColumnsMap = {
  mittente_denominazione: "Mittente",
  numero_documento: "N. Documento",
  data_documento: "Data Doc.",
  data_scadenza: "Scadenza",
  importo_imponibile: "Imponibile",
  importo_iva: "IVA",
  importo_totale: "Totale",
  stato_pagamento: "Stato",
  categoria: "Categoria",
  data_ricezione: "Ricezione",
};

const initialVisibleColumns = ['mittente_denominazione', 'numero_documento', 'data_documento', 'data_scadenza', 'importo_totale', 'stato_pagamento'];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const formatCurrency = (v) => `€ ${parseFloat(v || 0).toFixed(2)}`;

function isScaduta(fattura) {
  if (!fattura.data_scadenza) return false;
  if (fattura.stato_pagamento === 'pagata') return false;
  return new Date(fattura.data_scadenza) < new Date();
}

// --- Modal dettaglio/edit ---
function FatturaDetailModal({ fattura, onClose, onSaved, onRefresh }) {
  const [localFattura, setLocalFattura] = useState(fattura);
  const [form, setForm] = useState({
    stato_pagamento: fattura.stato_pagamento || 'da_pagare',
    data_scadenza: fattura.data_scadenza || '',
    data_pagamento_effettivo: fattura.data_pagamento_effettivo || '',
    categoria: fattura.categoria || '',
    note: fattura.note || '',
  });
  const [saving, setSaving] = useState(false);

  // Firefly state
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [assetAccountsLoading, setAssetAccountsLoading] = useState(false);
  const [fireflyMapping, setFireflyMapping] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [selectedExpenseId, setSelectedExpenseId] = useState('');
  const [expenseAccountNameOverride, setExpenseAccountNameOverride] = useState('');
  const [createIfMissing, setCreateIfMissing] = useState(false);
  const [fireflyRegistering, setFireflyRegistering] = useState(false);

  useEffect(() => {
    const loadFireflyData = async () => {
      setAssetAccountsLoading(true);
      try {
        const [accountsResult, expenseResult, mappingResult] = await Promise.allSettled([
          getFireflyAccounts('asset'),
          getFireflyAccounts('expense'),
          getFireflyAccountMapping(),
        ]);
        if (accountsResult.status === 'fulfilled') {
          setAssetAccounts(accountsResult.value.data.asset || []);
        }
        if (expenseResult.status === 'fulfilled') {
          setExpenseAccounts(expenseResult.value.data.expense || []);
        }
        if (mappingResult.status === 'fulfilled') {
          setFireflyMapping(mappingResult.value.data || null);
        }
      } finally {
        setAssetAccountsLoading(false);
      }
    };
    loadFireflyData();
  }, []);

  // Nome conto spesa dal mapping basato sulla categoria attualmente selezionata nel form
  const mappedExpenseAccountName = useMemo(() => {
    const cat = form.categoria;
    if (!cat || !fireflyMapping) return null;
    const field = CATEGORIA_TO_MAPPING_FIELD[cat];
    return field ? (fireflyMapping[field] || null) : null;
  }, [form.categoria, fireflyMapping]);

  // Quando cambia la categoria, pre-seleziona il conto spesa nel dropdown se esiste
  useEffect(() => {
    if (!mappedExpenseAccountName || expenseAccounts.length === 0) return;
    const found = expenseAccounts.find(a => a.attributes.name === mappedExpenseAccountName);
    if (found) {
      setSelectedExpenseId(found.id);
      setExpenseAccountNameOverride('');
    } else {
      setSelectedExpenseId('__manual__');
      setExpenseAccountNameOverride(mappedExpenseAccountName);
    }
  }, [mappedExpenseAccountName, expenseAccounts]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        stato_pagamento: form.stato_pagamento || null,
        data_scadenza: form.data_scadenza || null,
        data_pagamento_effettivo: form.data_pagamento_effettivo || null,
        categoria: form.categoria || null,
        note: form.note || null,
      };
      await updateFatturaPassiva(fattura.id, payload);
      toast.success('Fattura aggiornata!');
      onSaved();
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleFireflyRegister = async () => {
    if (!selectedAssetId) {
      toast.error('Seleziona un conto sorgente (asset)');
      return;
    }
    const selectedExpenseAccount = expenseAccounts.find(a => a.id === selectedExpenseId);
    const expenseName = selectedExpenseId === '__manual__'
      ? expenseAccountNameOverride
      : (selectedExpenseAccount?.attributes?.name || expenseAccountNameOverride || mappedExpenseAccountName);
    if (!expenseName) {
      toast.error('Inserisci o seleziona un conto di spesa destinazione');
      return;
    }
    setFireflyRegistering(true);
    try {
      const res = await registraFatturaPassivaFirefly(localFattura.id, {
        source_asset_account_id: selectedAssetId,
        expense_account_name: expenseName,
        create_if_missing: selectedExpenseId === '__manual__' ? createIfMissing : false,
      });
      setLocalFattura(res.data);
      if (onRefresh) onRefresh();
      toast.success('Fattura registrata su Firefly III!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la registrazione su Firefly');
    } finally {
      setFireflyRegistering(false);
    }
  };

  const handleFireflyAnnul = async () => {
    if (!window.confirm('Eliminare questa transazione da Firefly III?')) return;
    setFireflyRegistering(true);
    try {
      const res = await annullaRegistrazioneFatturaFirefly(localFattura.id);
      setLocalFattura(res.data);
      if (onRefresh) onRefresh();
      toast.success('Registrazione Firefly annullata');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'annullamento");
    } finally {
      setFireflyRegistering(false);
    }
  };

  // Righe dettaglio dal raw_data
  const righe = useMemo(() => {
    if (!fattura.raw_data) return [];
    try {
      const doc = fattura.raw_data?.dati_documento?.documento || {};
      const dettagli = doc.DettaglioLinee || [];
      return Array.isArray(dettagli) ? dettagli : [dettagli];
    } catch { return []; }
  }, [fattura.raw_data]);

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
          Fattura {fattura.numero_documento} — {fattura.mittente_denominazione}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={3}><small className="text-muted d-block">Data documento</small>{formatDate(fattura.data_documento)}</Col>
          <Col md={3}><small className="text-muted d-block">Data ricezione</small>{formatDate(fattura.data_ricezione)}</Col>
          <Col md={3}><small className="text-muted d-block">Imponibile</small>{formatCurrency(fattura.importo_imponibile)}</Col>
          <Col md={3}><small className="text-muted d-block">IVA</small>{formatCurrency(fattura.importo_iva)}</Col>
        </Row>
        <Row className="mb-3">
          <Col md={4}><small className="text-muted d-block fw-bold">Totale fattura</small><span style={{ fontSize: '1.2rem', color: '#0d6efd', fontWeight: 'bold' }}>{formatCurrency(fattura.importo_totale)}</span></Col>
          <Col md={4}><small className="text-muted d-block">SDI ID</small>{fattura.sdi_identificativo || '—'}</Col>
          <Col md={4}><small className="text-muted d-block">P.IVA mittente</small>{fattura.mittente_piva || '—'}</Col>
        </Row>

        {righe.length > 0 && (
          <>
            <h6 className="mt-3 mb-2">Righe della fattura</h6>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Descrizione</th>
                  <th className="text-end">Qtà</th>
                  <th className="text-end">Prezzo unit.</th>
                  <th className="text-end">Importo</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r, i) => (
                  <tr key={i}>
                    <td>{r.Descrizione || '—'}</td>
                    <td className="text-end">{r.Quantita || '—'}</td>
                    <td className="text-end">{r.PrezzoUnitario ? `€ ${parseFloat(r.PrezzoUnitario).toFixed(2)}` : '—'}</td>
                    <td className="text-end">{r.PrezzoTotale ? `€ ${parseFloat(r.PrezzoTotale).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        <hr />
        <h6 className="mb-3">Gestione pagamento</h6>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label>Stato pagamento</Form.Label>
              <Form.Select value={form.stato_pagamento} onChange={e => setForm(p => ({ ...p, stato_pagamento: e.target.value }))}>
                {STATO_PAGAMENTO_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label>Data scadenza</Form.Label>
              <Form.Control type="date" value={form.data_scadenza} onChange={e => setForm(p => ({ ...p, data_scadenza: e.target.value }))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label>Data pagamento effettivo</Form.Label>
              <Form.Control type="date" value={form.data_pagamento_effettivo} onChange={e => setForm(p => ({ ...p, data_pagamento_effettivo: e.target.value }))} />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label>Categoria spesa</Form.Label>
              <Form.Select value={form.categoria} onChange={e => setForm(p => ({ ...p, categoria: e.target.value }))}>
                {CATEGORIA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group className="mb-2">
              <Form.Label>Note</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} placeholder="Annotazioni, riferimenti interni..." />
            </Form.Group>
          </Col>
        </Row>

        <hr />
        <h6 className="mb-3">
          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2 text-warning" />
          Registrazione Firefly III
        </h6>

        {localFattura.firefly_transaction_id ? (
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Badge bg="success" className="p-2 fs-6">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              Registrata su Firefly (ID: {localFattura.firefly_transaction_id})
            </Badge>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={handleFireflyAnnul}
              disabled={fireflyRegistering}
            >
              {fireflyRegistering ? <Spinner size="sm" animation="border" /> : 'Annulla registrazione'}
            </Button>
          </div>
        ) : (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Conto sorgente (asset) <span className="text-danger">*</span></Form.Label>
                  {assetAccountsLoading ? (
                    <div className="d-flex align-items-center gap-2"><Spinner size="sm" animation="border" /><small>Caricamento conti Firefly...</small></div>
                  ) : assetAccounts.length === 0 ? (
                    <div className="text-muted small">Nessun conto asset trovato in Firefly</div>
                  ) : (
                    <Form.Select value={selectedAssetId} onChange={e => setSelectedAssetId(e.target.value)}>
                      <option value="">— Seleziona conto —</option>
                      {assetAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.attributes.name}</option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Conto di spesa (destinazione)</Form.Label>
                  {assetAccountsLoading ? null : expenseAccounts.length > 0 ? (
                    <div>
                      <Form.Select
                        value={selectedExpenseId}
                        onChange={e => {
                          setSelectedExpenseId(e.target.value);
                          if (e.target.value !== '__manual__') setExpenseAccountNameOverride('');
                        }}
                      >
                        <option value="">— Seleziona conto spesa —</option>
                        {expenseAccounts.map(acc => (
                          <option key={acc.id} value={acc.id}>{acc.attributes.name}</option>
                        ))}
                        <option value="__manual__">✏️ Inserisci manualmente...</option>
                      </Form.Select>
                      {selectedExpenseId === '__manual__' && (
                        <Form.Control
                          className="mt-1"
                          value={expenseAccountNameOverride}
                          onChange={e => setExpenseAccountNameOverride(e.target.value)}
                          placeholder="Nome conto spesa da creare in Firefly..."
                        />
                      )}
                      {mappedExpenseAccountName && selectedExpenseId !== '__manual__' && !expenseAccounts.find(a => a.id === selectedExpenseId && a.attributes.name === mappedExpenseAccountName) && (
                        <Form.Text className="text-muted">Suggerito dalla categoria: <strong>{mappedExpenseAccountName}</strong></Form.Text>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Form.Control
                        value={expenseAccountNameOverride}
                        onChange={e => setExpenseAccountNameOverride(e.target.value)}
                        placeholder="Nome conto spesa in Firefly..."
                      />
                      <Form.Text className="text-muted">
                        {form.categoria && mappedExpenseAccountName ? `Suggerito: ${mappedExpenseAccountName}` : 'Conti spesa Firefly non caricati.'}
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            {selectedExpenseId === '__manual__' && (
              <Form.Check
                type="checkbox"
                id="create-expense-if-missing"
                label="Crea il conto di spesa se non esiste in Firefly"
                checked={createIfMissing}
                onChange={e => setCreateIfMissing(e.target.checked)}
                className="mb-2"
              />
            )}
            <Button
              variant="warning"
              size="sm"
              onClick={handleFireflyRegister}
              disabled={fireflyRegistering || !selectedAssetId || assetAccountsLoading}
            >
              {fireflyRegistering
                ? <><Spinner size="sm" animation="border" className="me-1" />Registrazione in corso...</>
                : <><FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />Registra su Firefly III</>
              }
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Chiudi</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" animation="border" /> : 'Salva modifiche'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function StatoBadge({ fattura }) {
  const cfg = STATO_CONFIG[fattura.stato_pagamento] || { label: fattura.stato_pagamento, bg: 'secondary', icon: faClock };
  const scaduta = isScaduta(fattura);
  return (
    <span>
      <Badge bg={scaduta ? 'danger' : cfg.bg} className="me-1">
        <FontAwesomeIcon icon={cfg.icon} className="me-1" />
        {scaduta ? 'SCADUTA' : cfg.label}
      </Badge>
    </span>
  );
}

function FatturePassiveList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [fatture, setFatture] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [totaleDaPagare, setTotaleDaPagare] = useState(0);
  const [totalePagateMese, setTotalePagateMese] = useState(0);

  const [columnVisibility, setColumnVisibility] = useState(
    Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {})
  );
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-fatture-passive');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-fatture-passive');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-fatture-passive', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-fatture-passive', JSON.stringify(columnFilters));
  }, [columnFilters]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalFatture, setTotalFatture] = useState(0);

  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({
    stato_pagamento: '',
    categoria: '',
  });

  const buildFilters = useCallback(
    () => JSON.stringify(buildServerFilters(columnFilters)),
    [columnFilters]
  );

  const fetchFattureData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const response = await getFatturePassive(
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        sortParams,
        buildFilters()
      );
      setFatture(response.data.fatture_passive);
      setTotalFatture(response.data.total);
      setTotaleDaPagare(response.data.totale_da_pagare || 0);
      setTotalePagateMese(response.data.totale_pagate_mese || 0);
    } catch (err) {
      setError('Errore nel caricamento delle fatture passive.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, buildFilters]);

  useEffect(() => {
    fetchFattureData();
  }, [fetchFattureData]);

  const handleSync = async () => {
    if (!window.confirm("Avviare la sincronizzazione con Fattura24?")) return;
    setIsSyncing(true);
    setError(null);
    try {
      const response = await syncFatturePassive();
      toast.success(response.data.message || "Sincronizzazione completata!");
      fetchFattureData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Errore durante la sincronizzazione.";
      setError(errorMsg);
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await exportFatturePassiveExcel(buildFilters());
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fatture_passive_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Errore durante il download Excel');
    }
  };

  // Gestione selezione multipla
  const toggleSelectAll = () => {
    if (selectedIds.length === fatture.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(fatture.map(f => f.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkEdit = async () => {
    if (selectedIds.length === 0) return;
    if (!bulkEditForm.stato_pagamento && !bulkEditForm.categoria) {
      toast.error('Seleziona almeno un campo da modificare');
      return;
    }

    try {
      const updates = {};
      if (bulkEditForm.stato_pagamento) updates.stato_pagamento = bulkEditForm.stato_pagamento;
      if (bulkEditForm.categoria) updates.categoria = bulkEditForm.categoria;

      const promises = selectedIds.map(id => updateFatturaPassiva(id, updates));
      await Promise.all(promises);
      
      toast.success(`Aggiornate ${selectedIds.length} fatture`);
      setSelectedIds([]);
      setShowBulkEditModal(false);
      setBulkEditForm({ stato_pagamento: '', categoria: '' });
      fetchFattureData();
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento bulk');
    }
  };

  const handleBulkExport = async () => {
    if (selectedIds.length === 0) return;
    try {
      const selectedFilters = { ids: selectedIds };
      const res = await exportFatturePassiveExcel(JSON.stringify(selectedFilters));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fatture_passive_selezionate_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSelectedIds([]);
    } catch {
      toast.error('Errore durante l\'export delle fatture selezionate');
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => (
          <Form.Check
            type="checkbox"
            checked={selectedIds.length === fatture.length && fatture.length > 0}
            onChange={toggleSelectAll}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: info => (
          <Form.Check
            type="checkbox"
            checked={selectedIds.includes(info.row.original.id)}
            onChange={() => toggleSelectRow(info.row.original.id)}
            onClick={e => e.stopPropagation()}
          />
        ),
      }),
      ...Object.entries(allColumnsMap).map(([key, label]) =>
        columnHelper.accessor(key, {
          header: label,
          enableColumnFilter: key !== 'importo_imponibile' && key !== 'importo_iva',
          meta: FILTER_VARIANTS[key] || { filterVariant: 'text' },
          cell: info => {
            const row = info.row.original;
            const v = info.getValue();
            if (key === 'stato_pagamento') return <StatoBadge fattura={row} />;
            if (key === 'data_scadenza') {
              if (!v) return <span className="text-muted">—</span>;
              const scaduta = isScaduta(row);
              return <span className={scaduta ? 'text-danger fw-bold' : ''}>{formatDate(v)}</span>;
            }
            if (key.startsWith('importo')) return formatCurrency(v);
            if (key.startsWith('data')) return formatDate(v);
            if (key === 'categoria') {
              const opt = CATEGORIA_OPTIONS.find(o => o.value === v);
              return opt ? opt.label : (v || '—');
            }
            return v || '—';
          },
        })
      ),
      columnHelper.display({
        id: 'azioni',
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        cell: info => (
          <Button size="sm" variant="outline-primary" onClick={() => setSelectedFattura(info.row.original)}>
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        ),
      }),
    ],
    [selectedIds, fatture]
  );

  const table = useReactTable({
    data: fatture,
    columns,
    state: { columnVisibility, sorting, columnFilters },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering: true,
    manualSorting: true,
  });

  const hasActiveFilters = columnFilters.length > 0;

  const resetFilters = () => {
    table.resetColumnFilters();
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalFatture / itemsPerPage);

  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>
      );
    }
    return items;
  };

  const renderFatturaCard = useCallback((fattura) => (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h6 className="mb-1">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-muted" />
            {fattura.numero_documento || 'N/A'}
          </h6>
          <small className="text-muted">{fattura.mittente_denominazione || 'N/A'}</small>
        </div>
        <StatoBadge fattura={fattura} />
      </div>
      <Row className="mb-2">
        <Col xs={6}>
          <small className="text-muted d-block">Totale</small>
          <strong style={{ fontSize: '1.1rem', color: '#0d6efd' }}>{formatCurrency(fattura.importo_totale)}</strong>
        </Col>
        <Col xs={6}>
          <small className="text-muted d-block">Scadenza</small>
          <span className={isScaduta(fattura) ? 'text-danger fw-bold' : ''}>{formatDate(fattura.data_scadenza)}</span>
        </Col>
      </Row>
      {fattura.categoria && (
        <div><small className="text-muted">Cat.: </small><span>{CATEGORIA_OPTIONS.find(o => o.value === fattura.categoria)?.label || fattura.categoria}</span></div>
      )}
      {fattura.note && <div className="mt-1"><small className="text-muted fst-italic">{fattura.note}</small></div>}
      <div className="mt-2">
        <Button size="sm" variant="outline-primary" onClick={() => setSelectedFattura(fattura)}>
          <FontAwesomeIcon icon={faEdit} className="me-1" />Dettaglio / Modifica
        </Button>
      </div>
    </div>
  ), []);

  const actionsDropdownContent = useMemo(() => (
    <>
      <Dropdown.Item onClick={handleSync} disabled={isSyncing}>
        <FontAwesomeIcon icon={faSync} className={`me-2 ${isSyncing ? 'fa-spin' : ''}`} />
        {isSyncing ? 'Sincronizzazione...' : 'Sincronizza con Fattura24'}
      </Dropdown.Item>
      <Dropdown.Item onClick={handleExportExcel}>
        <FontAwesomeIcon icon={faFileExcel} className="me-2 text-success" />
        Esporta Excel
      </Dropdown.Item>
    </>
  ), [isSyncing]);

  return (
    <>
      {selectedFattura && (
        <FatturaDetailModal
          fattura={selectedFattura}
          onClose={() => setSelectedFattura(null)}
          onSaved={() => { setSelectedFattura(null); fetchFattureData(); }}
          onRefresh={fetchFattureData}
        />
      )}

      {/* Modal Modifica Bulk */}
      <Modal show={showBulkEditModal} onHide={() => setShowBulkEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Modifica {selectedIds.length} fatture
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Stato pagamento</Form.Label>
            <Form.Select 
              value={bulkEditForm.stato_pagamento} 
              onChange={e => setBulkEditForm({...bulkEditForm, stato_pagamento: e.target.value})}
            >
              <option value="">— Non modificare —</option>
              {STATO_PAGAMENTO_OPTIONS.filter(o => o.value).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Categoria</Form.Label>
            <Form.Select 
              value={bulkEditForm.categoria} 
              onChange={e => setBulkEditForm({...bulkEditForm, categoria: e.target.value})}
            >
              <option value="">— Non modificare —</option>
              {CATEGORIA_OPTIONS.filter(o => o.value).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Alert variant="info" className="mb-0">
            <small>Seleziona i campi da modificare. I campi lasciati su "Non modificare" rimarranno invariati.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkEditModal(false)}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleBulkEdit}>
            <FontAwesomeIcon icon={faEdit} className="me-1" />
            Aggiorna {selectedIds.length} fatture
          </Button>
        </Modal.Footer>
      </Modal>

      <Row className="mb-3 align-items-center">
        <Col>
          <h1 className="h3 mb-0">Fatture Passive</h1>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          {hasActiveFilters && (
            <Button variant="outline-secondary" onClick={resetFilters}>
              Azzera filtri <Badge bg="danger" className="ms-1">{columnFilters.length}</Badge>
            </Button>
          )}
          <Button variant="outline-success" onClick={handleExportExcel}>
            <FontAwesomeIcon icon={faFileExcel} className="me-1" />Excel
          </Button>
          <Button onClick={handleSync} disabled={isSyncing} variant="primary">
            {isSyncing ? <><Spinner as="span" animation="border" size="sm" /> Sync...</> : <><FontAwesomeIcon icon={faSync} className="me-1" />Sincronizza</>}
          </Button>
        </Col>
      </Row>

      {/* Header totali */}
      <Row className="mb-3">
        <Col md={4}>
          <Card className="border-warning">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Da pagare (totale)</small>
              <span className="fs-5 fw-bold text-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {formatCurrency(totaleDaPagare)}
              </span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-success">
            <Card.Body className="py-2">
              <small className="text-muted d-block">Pagate questo mese</small>
              <span className="fs-5 fw-bold text-success">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                {formatCurrency(totalePagateMese)}
              </span>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body className="py-2">
              <small className="text-muted d-block">Totale fatture (filtro)</small>
              <span className="fs-5 fw-bold text-primary">{totalFatture} fatture</span>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Barra azioni bulk */}
      {selectedIds.length > 0 && !isMobile && (
        <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
          <span>
            <strong>{selectedIds.length}</strong> fattura/e selezionata/e
          </span>
          <div>
            <Button size="sm" variant="warning" onClick={() => setShowBulkEditModal(true)} className="me-2">
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Modifica ({selectedIds.length})
            </Button>
            <Button size="sm" variant="success" onClick={handleBulkExport} className="me-2">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              Esporta selezionate
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setSelectedIds([])}>
              Deseleziona tutto
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0 p-md-3">
          {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {!loading && !error && (
            <>
              {isMobile ? (
                <MobileListLayout
                  items={fatture}
                  renderCard={renderFatturaCard}
                  entityName="fattura"
                  actionsDropdownContent={actionsDropdownContent}
                  showCreateButton={false}
                  showSelectAll={false}
                />
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <SortableHeader key={header.id} header={header} table={table} stickyColumns={[]} />
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.length === 0 ? (
                        <tr><td colSpan={Object.keys(allColumnsMap).length + 2} className="text-center text-muted py-4">Nessuna fattura trovata</td></tr>
                      ) : (
                        table.getRowModel().rows.map(row => (
                          <tr key={row.id} style={{ cursor: 'pointer' }} onDoubleClick={() => setSelectedFattura(row.original)}>
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
        <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Form.Group as={Row} className="align-items-center mb-0">
            <Col xs="auto"><Form.Label className="mb-0 small">Righe</Form.Label></Col>
            <Col xs="auto">
              <Form.Select size="sm" value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
            <Col xs="auto"><small className="text-muted">Totale: {totalFatture}</small></Col>
          </Form.Group>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
            {renderPaginationItems()}
            <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} />
          </Pagination>
        </Card.Footer>
      </Card>
    </>
  );
}

export default FatturePassiveList;
